import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { Resource } from '@retail/auth/types';
import { useAppTFunction } from '@retail/app/i18n';
import { useMatch } from 'react-router';
import { mgPrisRoutes } from '@retail/mgpris/config';
import { Route } from './AppSidebar';
import { useMemo } from 'react';
import {
  BriefcaseIcon,
  BriefcaseIconFilled,
  CalculatorFillIcon,
  CalculatorIcon,
  DownloadIconMenu,
  DownloadIconMenuFilled,
  HouseIcon,
  HouseNoFillIcon,
  MarketIcon,
  MarketIconFilled,
  ShoppingCartIcon,
  ShoppingCartIconFilled,
} from '@shared/custom-icons';
import { AutoStories, AutoStoriesOutlined, ToggleOn } from '@mui/icons-material';
import { GoRepoLocked } from 'react-icons/go';
import DashboardIcon from '@mui/icons-material/Dashboard';
import GridViewIcon from '@mui/icons-material/GridView';
import FileCopy from '@mui/icons-material/FileCopy';
import FileCopyOutlined from '@mui/icons-material/FileCopyOutlined';
import Campaign from '@mui/icons-material/Campaign';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import LocalOffer from '@mui/icons-material/LocalOffer';
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined';
import { MgPrisFeature } from '@retail/mgpris/config-types';
import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { isFeatureEnabled } from '@retail/utils';

const RepoLockedIcon = () => <GoRepoLocked size="17" />;

export type ResourceRoute = Route & {
  resource: Resource;
  feature?: MgPrisFeature;
};

export const useNavRoutes = (): Route[] => {
  const { userPermissions } = usePermissionsForCurrentUser();
  const t = useAppTFunction();
  const { featureFlags } = useMgPrisConfig();
  const { appTenant, orgUnitType } = useSelectedOrgUnit();

  const dashboardRouteMatch = useMatch(`${mgPrisRoutes.dashboard.fullRoutePath}/*`);
  const assortmentRouteMatch = useMatch(`${mgPrisRoutes.assortment.fullRoutePath}/*`);
  const priceCalculationRouteMatch = useMatch(`${mgPrisRoutes.calculus.fullRoutePath}/*`);
  const discountsRouteMatch = useMatch(`${mgPrisRoutes.discounts.fullRoutePath}/*`);
  const itemsRouteMatch = useMatch(`${mgPrisRoutes.items.fullRoutePath}/*`);
  const marketInsightRouteMatch = useMatch(`${mgPrisRoutes.marketInsight.fullRoutePath}/*`);
  const administrationRouteMatch = useMatch(`${mgPrisRoutes.administration.fullRoutePath}/*`);
  const itemExportRouteMatch = useMatch(`${mgPrisRoutes.export.fullRoutePath}/*`);
  const featureTogglesRouteMatch = useMatch(`${mgPrisRoutes.superuser.features.fullRoutePath}/*`);
  const permissionsRouteMatch = useMatch(`${mgPrisRoutes.superuser.permissions.fullRoutePath}/*`);
  const campaignsRouteMatch = useMatch(`${mgPrisRoutes.campaigns.fullRoutePath}/*`);
  const centralAssortmentsRouteMatch = useMatch(
    `${mgPrisRoutes.centralAssortments.fullRoutePath}/*`
  );
  const supplierItemInsightReportRouteMatch = useMatch(
    `${mgPrisRoutes.insightReport.fullRoutePath}/*`
  );

  const allRoutes: ResourceRoute[] = useMemo(
    () => [
      {
        title: t('app.routes.dashboard'),
        Icon: dashboardRouteMatch ? DashboardIcon : GridViewIcon,
        to: mgPrisRoutes.dashboard.getFullLinkPath(),
        isActive: !!dashboardRouteMatch,
        resource: 'DASHBOARD',
        feature: 'new-dashboard',
      },
      {
        title: t('app.routes.assortment'),
        Icon: assortmentRouteMatch ? HouseIcon : HouseNoFillIcon,
        to: mgPrisRoutes.assortment.getFullLinkPath(),
        isActive: !!assortmentRouteMatch,
        resource: 'ASSORTMENT',
      },
      {
        title: t('app.routes.assortment'),
        Icon: assortmentRouteMatch ? HouseIcon : HouseNoFillIcon,
        to: mgPrisRoutes.assortment.getFullLinkPath(),
        isActive: !!assortmentRouteMatch,
        resource: 'CENTRAL_ASSORTMENT',
      },
      {
        title: t('app.routes.calculus'),
        Icon: priceCalculationRouteMatch ? CalculatorFillIcon : CalculatorIcon,
        to: mgPrisRoutes.calculus.getFullLinkPath(),
        isActive: !!priceCalculationRouteMatch,
        resource: 'PRICE_CALCULATION',
      },
      {
        title: t('app.routes.discounts'),
        Icon: discountsRouteMatch ? LocalOffer : LocalOfferOutlined,
        to: mgPrisRoutes.discounts.getFullLinkPath(),
        isActive: !!discountsRouteMatch,
        resource: 'PRICE_CALCULATION__CUSTOMER_MARKUP',
        feature: 'discount-matrix',
      },
      {
        title: t('app.routes.productSearch'),
        to: mgPrisRoutes.items.getFullLinkPath(),
        Icon: itemsRouteMatch ? ShoppingCartIconFilled : ShoppingCartIcon,
        isActive: !!itemsRouteMatch,
        resource: 'ITEM_SEARCH',
      },
      {
        title: t('app.routes.centralAssortments'),
        to: mgPrisRoutes.centralAssortments.getFullLinkPath(),
        Icon: () =>
          centralAssortmentsRouteMatch ? (
            <FileCopy fontSize="small" />
          ) : (
            <FileCopyOutlined fontSize="small" />
          ),
        isActive: !!centralAssortmentsRouteMatch,
        resource: 'CENTRAL_ASSORTMENT_MEMBER_VIEW',
      },
      {
        title: t('app.routes.marketInsight'),
        to: mgPrisRoutes.marketInsight.getFullLinkPath(),
        Icon: marketInsightRouteMatch ? MarketIconFilled : MarketIcon,
        isActive: !!marketInsightRouteMatch,
        resource: 'MARKET_INSIGHT',
      },
      {
        title: t('app.routes.administration'),
        to: mgPrisRoutes.administration.getFullLinkPath(),
        Icon: administrationRouteMatch ? BriefcaseIconFilled : BriefcaseIcon,
        isActive: !!administrationRouteMatch,
        resource: 'ADMINISTRATION',
      },
      {
        title: t('campaign.campaignSingular'),
        to: mgPrisRoutes.campaigns.getFullLinkPath(),
        Icon: campaignsRouteMatch
          ? () => <Campaign fontSize="medium" />
          : () => <CampaignOutlined fontSize="medium" />,
        isActive: !!campaignsRouteMatch,
        resource: 'CAMPAIGN',
      },
      {
        title: t('app.routes.export'),
        to: mgPrisRoutes.export.getFullLinkPath(),
        Icon: itemExportRouteMatch ? DownloadIconMenuFilled : DownloadIconMenu,
        isActive: !!itemExportRouteMatch,
        resource: 'ITEM_EXPORT',
      },
      {
        title: t('app.routes.insightReport'),
        to: mgPrisRoutes.insightReport.getFullLinkPath(),
        Icon: supplierItemInsightReportRouteMatch ? AutoStories : AutoStoriesOutlined,
        isActive: !!supplierItemInsightReportRouteMatch,
        resource: 'SUPPLIER_ITEMS_INSIGHT_REPORT',
      },
      {
        title: t('app.routes.features'),
        to: mgPrisRoutes.superuser.features.getFullLinkPath(),
        Icon: ToggleOn,
        isActive: !!featureTogglesRouteMatch,
        resource: 'FEATURE_TOGGLES',
      },
      {
        title: t('app.routes.permissions'),
        to: mgPrisRoutes.superuser.permissions.getFullLinkPath(),
        Icon: RepoLockedIcon,
        isActive: !!permissionsRouteMatch,
        resource: 'PERMISSIONS',
      },
    ],
    [
      administrationRouteMatch,
      assortmentRouteMatch,
      campaignsRouteMatch,
      centralAssortmentsRouteMatch,
      dashboardRouteMatch,
      discountsRouteMatch,
      featureTogglesRouteMatch,
      itemExportRouteMatch,
      itemsRouteMatch,
      marketInsightRouteMatch,
      permissionsRouteMatch,
      priceCalculationRouteMatch,
      supplierItemInsightReportRouteMatch,
      t,
    ]
  );

  return useMemo(
    () =>
      allRoutes
        .filter(({ resource }) =>
          userPermissions.some(
            (permission) => permission.resource === resource && permission.canRead()
          )
        )
        .filter(({ feature }) =>
          feature
            ? isFeatureEnabled({
                features: featureFlags,
                name: feature,
                orgUnitType,
                country: appTenant.countryCode,
              })
            : true
        ),
    [allRoutes, userPermissions, featureFlags, orgUnitType, appTenant.countryCode]
  );
};
